<script setup>
import { useAlert } from 'dashboard/composables';
import AddCanned from './AddCanned.vue';
import EditCanned from './EditCanned.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { debounce } from '@chatwoot/utils';
import AuthorStamp from './AuthorStamp.vue';

defineOptions({
  name: 'CannedResponseSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { isAdmin } = useAdmin();
const { t } = useI18n();

const { getPlainText } = useMessageFormatter();

const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const cannedResponseAPI = ref({ message: '' });

const searchQuery = ref('');
const sortOrder = ref('asc');
const collapsedFolders = ref({});

const records = computed(() =>
  getters.getSortedCannedResponses.value(sortOrder.value)
);
const uiFlags = computed(() => getters.getUIFlags.value);

const uncategorizedLabel = computed(() => t('CANNED_MGMT.LIST.UNCATEGORIZED'));

const groupedRecords = computed(() => {
  const groups = {};
  records.value.forEach(item => {
    const key = item.category || uncategorizedLabel.value;
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return Object.keys(groups)
    .sort((a, b) => {
      if (a === uncategorizedLabel.value) return 1;
      if (b === uncategorizedLabel.value) return -1;
      return a.localeCompare(b);
    })
    .map(category => ({ category, items: groups[category] }));
});

const summaryLabel = computed(() =>
  t('CANNED_MGMT.LIST.SUMMARY', {
    responses: records.value.length,
    folders: groupedRecords.value.length,
  })
);

const isFolderOpen = category => !collapsedFolders.value[category];
const toggleFolder = category => {
  collapsedFolders.value[category] = !collapsedFolders.value[category];
};

const tableHeaders = computed(() => [
  t('CANNED_MGMT.LIST.TABLE_HEADER.SHORT_CODE'),
  t('CANNED_MGMT.LIST.TABLE_HEADER.CONTENT'),
  t('CANNED_MGMT.LIST.TABLE_HEADER.AUTHOR'),
  t('CANNED_MGMT.LIST.TABLE_HEADER.USAGE_COUNT'),
  t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS'),
]);

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse', { searchKey: searchQuery.value });
  } catch (error) {
    // Ignore Error
  }
};

const debouncedSearch = debounce(fetchCannedResponses, 500);

onMounted(() => {
  fetchCannedResponses();
});

const showAlertMessage = message => {
  loading[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCannedResponse(activeResponse.value.id);
};
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('CANNED_MGMT.HEADER')"
      :description="$t('CANNED_MGMT.DESCRIPTION')"
      :link-text="$t('CANNED_MGMT.LEARN_MORE')"
      feature-name="canned_responses"
    >
      <template #actions>
        <Button
          icon="i-lucide-circle-plus"
          :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
          @click="openAddPopup"
        />
      </template>
    </BaseSettingsHeader>

    <div class="mt-6 flex-1">
      <div class="flex flex-wrap justify-between items-center gap-3 mb-5">
        <span v-if="records.length" class="text-sm text-n-slate-11">
          {{ summaryLabel }}
        </span>
        <span v-else />
        <Input
          v-model="searchQuery"
          :placeholder="$t('CANNED_MGMT.LIST.SEARCH_PLACEHOLDER')"
          class="w-full sm:w-72"
          size="sm"
          type="search"
          @input="debouncedSearch"
        />
      </div>

      <woot-loading-state
        v-if="uiFlags.fetchingList"
        :message="$t('CANNED_MGMT.LOADING')"
      />
      <p
        v-else-if="!records.length"
        class="flex flex-col items-center justify-center h-full text-base text-n-slate-11 py-10"
      >
        {{
          searchQuery
            ? $t('CANNED_MGMT.SEARCH_404')
            : $t('CANNED_MGMT.LIST.404')
        }}
      </p>

      <div v-else class="flex flex-col gap-3">
        <div
          v-for="group in groupedRecords"
          :key="group.category"
          class="rounded-xl border border-n-weak overflow-hidden bg-n-background"
        >
          <button
            class="w-full flex items-center gap-2.5 px-4 py-3 bg-n-alpha-2 hover:bg-n-alpha-3 transition-colors text-start"
            @click="toggleFolder(group.category)"
          >
            <fluent-icon
              :icon="
                isFolderOpen(group.category) ? 'chevron-down' : 'chevron-right'
              "
              class="size-4 text-n-slate-11 shrink-0"
            />
            <span class="font-semibold text-n-slate-12">
              {{ group.category }}
            </span>
            <span
              class="rounded-full bg-n-solid-1 border border-n-weak px-2 py-0.5 text-xs font-medium text-n-slate-11 tabular-nums"
            >
              {{ group.items.length }}
            </span>
          </button>

          <div v-if="isFolderOpen(group.category)" class="overflow-x-auto">
            <table class="min-w-full divide-y divide-n-weak">
              <thead>
                <tr>
                  <th
                    v-for="(thHeader, i) in tableHeaders"
                    :key="thHeader"
                    class="py-2.5 px-4 text-xs font-medium text-n-slate-10 whitespace-nowrap"
                    :class="
                      i === tableHeaders.length - 1 ? 'text-end' : 'text-start'
                    "
                  >
                    {{ thHeader }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak text-n-slate-11">
                <tr
                  v-for="(cannedItem, index) in group.items"
                  :key="cannedItem.id"
                  class="hover:bg-n-alpha-1 transition-colors"
                >
                  <td
                    class="py-3 px-4 align-top max-w-[10rem] font-medium text-n-slate-12"
                    :title="cannedItem.short_code"
                  >
                    <div class="truncate">{{ cannedItem.short_code }}</div>
                  </td>
                  <td
                    class="py-3 px-4 align-top max-w-md text-sm whitespace-normal break-words"
                  >
                    <div class="line-clamp-3">
                      {{ getPlainText(cannedItem.content) }}
                    </div>
                    <span
                      v-if="
                        cannedItem.attachments && cannedItem.attachments.length
                      "
                      class="mt-1 inline-flex items-center gap-1 text-xs text-n-slate-10"
                    >
                      <fluent-icon icon="attach" class="size-3" />
                      {{ cannedItem.attachments.length }}
                    </span>
                  </td>
                  <td class="py-3 px-4 align-top">
                    <AuthorStamp
                      :created-by="cannedItem.created_by"
                      :updated-by="cannedItem.updated_by"
                      :updated-at="cannedItem.updated_at"
                    />
                  </td>
                  <td class="py-3 px-4 align-top tabular-nums text-n-slate-11">
                    {{ cannedItem.usage_count }}
                  </td>
                  <td class="py-3 px-4 align-top">
                    <div class="flex justify-end gap-1">
                      <Button
                        v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                        icon="i-lucide-pen"
                        slate
                        xs
                        faded
                        @click="openEditPopup(cannedItem)"
                      />
                      <Button
                        v-if="isAdmin"
                        v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                        icon="i-lucide-trash-2"
                        xs
                        ruby
                        faded
                        :is-loading="loading[cannedItem.id]"
                        @click="openDeletePopup(cannedItem, index)"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddCanned :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditCanned
        v-if="showEditPopup"
        :id="activeResponse.id"
        :edshort-code="activeResponse.short_code"
        :edcontent="activeResponse.content"
        :edcategory="activeResponse.category"
        :edattachments="activeResponse.attachments"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>
